import type { CrowdexPluginDetails } from '@crowdex/plugin-contracts';
import { Button } from '@crowdex/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@crowdex/ui/components/dialog';
import { Switch } from '@crowdex/ui/components/switch';
import { useState } from 'react';

type UnsandboxedCommand = NonNullable<CrowdexPluginDetails['unsandboxedCommands']>[number];

/**
 * On a machine without a sandbox for commands (Windows), the plugin's commands, each run only
 * once the user allowed it here. Allowing asks first: the program then runs with the user's
 * access to their files and the network.
 */
export function UnsandboxedCommands({
  pluginTitle,
  commands,
  busy,
  onChange,
}: {
  pluginTitle: string;
  commands: readonly UnsandboxedCommand[];
  busy: boolean;
  onChange: (command: string, allowed: boolean) => void;
}) {
  const [asking, setAsking] = useState<UnsandboxedCommand | null>(null);
  return (
    <>
      <p className="mb-3 text-muted-foreground text-xs">
        Windows has no sandbox for the programs plugins run. A command you allow runs with your
        access to your files and the network.
      </p>
      <div className="divide-y divide-border overflow-hidden rounded-panel border border-border">
        {commands.map((command) => (
          <div
            className="flex items-center justify-between gap-4 bg-background/55 px-4 py-3"
            key={command.id}
          >
            <span className="min-w-0">
              <span className="block truncate text-sm">{command.title}</span>
              <span className="mt-0.5 block truncate font-mono text-muted-foreground text-xs">
                {command.program}
              </span>
            </span>
            <Switch
              aria-label={`${command.allowed ? 'Stop allowing' : 'Allow'} ${command.title} without a sandbox`}
              checked={command.allowed}
              disabled={busy}
              onCheckedChange={(checked) =>
                checked ? setAsking(command) : onChange(command.id, false)
              }
            />
          </div>
        ))}
      </div>
      <Dialog onOpenChange={(open) => !open && setAsking(null)} open={asking !== null}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Run “{asking?.title}” without a sandbox?</DialogTitle>
            <DialogDescription>
              {pluginTitle} will run <span className="font-mono">{asking?.program}</span> on this
              computer with your access to your files and the network. Allow it only if you trust
              this plugin.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setAsking(null)} variant="ghost">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (asking !== null) onChange(asking.id, true);
                setAsking(null);
              }}
              variant="destructive"
            >
              Allow
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
